import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip, Typography } from '@mui/material';
import newIdempotencyKey from 'src/utils/idempotencyKey';
import { claimCall } from './teamMember.api';

// One tap on the live call pill: "I'm on this call". The server refuses a call that cannot be
// claimed (missed, older than 2 hours, not a desk-phone call) with a 400 `detail`, and answers
// 409 `already_named` when a stronger source (desk, handset) already names someone: then it is
// either you ("You're on this call") or someone else ("Already shows <name>"), and the button
// goes. Messages stay on screen (never hover-only), cut short to fit the pill.
export default function ImOnThisCallButton({ practiceId, callId, onClaimed, size, onDark, compact, mePersonId }) {
  const [state, setState] = useState({ status: 'idle', message: '' });
  const key = useRef({ callId: null, value: null });
  const busy = state.status === 'busy';

  const claim = async () => {
    if (busy || state.status === 'done' || state.status === 'other') return;
    // One key per call, reused if the tap is repeated after a refusal.
    if (key.current.callId !== callId) key.current = { callId, value: newIdempotencyKey() };
    setState({ status: 'busy', message: '' });
    const result = await claimCall(practiceId, callId, { idempotencyKey: key.current.value });
    if (result.ok) {
      setState({ status: 'done', message: '' });
      if (onClaimed) onClaimed(result.data);
    } else if (result.alreadyNamed) {
      const named = result.row?.user ?? null;
      if (named !== null && mePersonId != null && String(named) === String(mePersonId)) {
        setState({ status: 'done', message: '' });
        if (onClaimed && result.row) onClaimed(result.row);
      } else {
        const name = (typeof result.row?.user_name === 'string' && result.row.user_name.trim()) || '';
        setState({ status: 'other', message: name ? `Already shows ${name}` : 'Already shows someone else' });
      }
    } else if (result.uncertain) {
      setState({ status: 'uncertain', message: "Couldn't confirm — refresh to check." });
    } else {
      setState({ status: 'error', message: result.message });
    }
  };

  const textSx = { fontWeight: 600, color: onDark ? 'inherit' : undefined, flexShrink: 0 };
  if (state.status === 'done') {
    return <Typography role="status" variant="caption" noWrap sx={{ ...textSx, color: onDark ? 'inherit' : 'success.dark' }} data-testid="im-on-call-done">You&apos;re on this call</Typography>;
  }
  if (state.status === 'other') {
    return <Typography role="status" variant="caption" noWrap title={state.message} sx={{ ...textSx, maxWidth: compact ? 170 : undefined, flexShrink: 1 }} data-testid="im-on-call-other">{state.message}</Typography>;
  }
  const darkSx = onDark ? {
    flexShrink: 0, borderRadius: 999, color: 'common.white', borderColor: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'rgba(255,255,255,0.85)', color: 'grey.900' },
  } : {};
  let messageColor = 'text.secondary';
  if (onDark) messageColor = 'inherit';
  else if (state.status === 'error') messageColor = 'error';
  const message = state.message && (
    <Typography
      role="alert"
      variant="caption"
      noWrap={compact}
      title={state.message}
      color={messageColor}
      sx={compact ? { maxWidth: 170, flexShrink: 1, fontWeight: 600 } : { display: 'block' }}
      data-testid="im-on-call-message"
    >
      {state.message}
    </Typography>
  );
  return (
    <>
      <Tooltip title="Tell the team you are the one on this call" arrow>
        <span>
          <Button size={size} variant="outlined" color={onDark ? 'inherit' : 'primary'} onClick={claim} disabled={busy || state.status === 'uncertain'} sx={{ textTransform: 'none !important', ...darkSx }}>
            {busy ? 'Saving…' : "I'm on this call"}
          </Button>
        </span>
      </Tooltip>
      {message}
    </>
  );
}

ImOnThisCallButton.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  // The pill's call row id, like "voice:123".
  callId: PropTypes.string.isRequired,
  // (updatedRow) after a successful claim.
  onClaimed: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium']),
  // On the dark call pill: white outline, messages cut short beside the button.
  onDark: PropTypes.bool,
  compact: PropTypes.bool,
  // The signed-in user's PracticeUser id, to tell "already you" from "already someone else".
  mePersonId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
ImOnThisCallButton.defaultProps = { onClaimed: undefined, size: 'small', onDark: false, compact: false, mePersonId: null };
